import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { ShieldCheck, Truck, Undo2, Minus, Plus, ArrowLeft } from "lucide-react";
import Stars from "@/components/Stars";
import ProductReviews from "@/components/ProductReviews";
import { formatIDR } from "@/lib/format";
import { useLang } from "@/context/LanguageContext";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { t } = useLang();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get(`/products/${id}`)
      .then((r) => setProduct(r.data))
      .catch(() => setError("notfound"))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="max-w-7xl mx-auto px-6 py-20 text-zinc-500">{t("product.loading")}</div>;
  if (error || !product) return (
    <div className="max-w-7xl mx-auto px-6 py-20 text-center">
      <div className="text-lg font-semibold text-zinc-900">{t("product.notFound")}</div>
      <Link to="/shop" className="mt-4 inline-block text-brand hover:underline">{t("product.backToShop")}</Link>
    </div>
  );

  return (
    <main data-testid="product-detail-page" className="max-w-7xl mx-auto px-6 md:px-10 py-8">
      <button data-testid="back-to-shop" onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 mb-6 transition-colors">
        <ArrowLeft className="w-4 h-4" /> {t("product.back")}
      </button>

      <div className="grid md:grid-cols-2 gap-10 lg:gap-16">
        <div className="rounded-3xl product-image-bg aspect-square overflow-hidden">
          <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
        </div>

        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">{product.category}</div>
          <h1 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tighter text-zinc-900 leading-tight">
            {product.name}
          </h1>
          {product.rating_count > 0 && (
            <div className="mt-3 flex items-center gap-2">
              <Stars value={product.rating_avg} size={16} />
              <a href="#reviews" className="text-sm text-zinc-600 hover:text-brand transition-colors">
                {product.rating_avg.toFixed(1)} · {product.rating_count} {t("product.reviews")}
              </a>
            </div>
          )}
          <div className="mt-5 font-display font-bold text-3xl text-zinc-900" data-testid="product-price">{formatIDR(product.price)}</div>
          <p className="mt-5 text-zinc-600 leading-relaxed">{product.description}</p>

          <div className="mt-8 flex items-center gap-3">
            <div className="flex items-center gap-2 rounded-full bg-zinc-100 p-1">
              <button data-testid="qty-dec" onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-9 h-9 rounded-full bg-white shadow-sm flex items-center justify-center">
                <Minus className="w-4 h-4" />
              </button>
              <span className="w-8 text-center font-semibold" data-testid="qty-value">{qty}</span>
              <button data-testid="qty-inc" onClick={() => setQty((q) => q + 1)} className="w-9 h-9 rounded-full bg-white shadow-sm flex items-center justify-center">
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <Button
              data-testid="add-to-cart-detail-btn"
              onClick={() => addItem(product, qty)}
              className="flex-1 rounded-full bg-brand hover:bg-brand-hover text-white py-6 text-base font-semibold"
            >
              {t("product.addToCart")} — {formatIDR(product.price * qty)}
            </Button>
          </div>

          <div className="mt-8 pt-8 border-t border-zinc-100 grid grid-cols-3 gap-4 text-sm">
            <div className="flex flex-col items-start gap-1.5">
              <ShieldCheck className="w-5 h-5 text-brand" />
              <div className="font-semibold text-zinc-900">{t("product.warranty")}</div>
            </div>
            <div className="flex flex-col items-start gap-1.5">
              <Truck className="w-5 h-5 text-brand" />
              <div className="font-semibold text-zinc-900">{t("product.freeShipping")}</div>
            </div>
            <div className="flex flex-col items-start gap-1.5">
              <Undo2 className="w-5 h-5 text-brand" />
              <div className="font-semibold text-zinc-900">{t("product.thirtyDayReturns")}</div>
            </div>
          </div>

          {product.brand && (
            <div className="mt-6 text-xs text-zinc-500">
              {t("product.brand")}: <span className="font-semibold text-zinc-800">{product.brand}</span> · {t("product.stock")}:{" "}
              <span className="font-semibold text-zinc-800">{product.stock}</span>
            </div>
          )}
        </div>
      </div>

      <div id="reviews">
        <ProductReviews productId={product.id} onRatingChange={(avg, count) => setProduct((p) => p ? { ...p, rating_avg: avg, rating_count: count } : p)} />
      </div>
    </main>
  );
}
